import React, { useCallback, useEffect, useState } from "react";
import { CaptionsText } from "./Captions.elements";
import { parseSync, NodeCue, NodeList, Cue } from "subtitle";
import { sanitize } from "../../util/html";

interface SlideCaptionsProps {
  captionsURL: string;
  position: number;
  hasNoAudio?: boolean;
}

export default function Captions({
  captionsURL,
  position,
  hasNoAudio = false,
}: SlideCaptionsProps): JSX.Element | null {
  const [parsed, setParsed] = useState<NodeList>();
  const [text, setText] = useState("\n\n");

  const isActive = useCallback(
    (cue: NodeCue): boolean => {
      const positionMs = position * 1000;
      return positionMs >= cue.data.start && positionMs < cue.data.end;
    },
    [position],
  );

  function getText(cue: Cue): string {
    return cue.text;
  }

  useEffect(() => {
    const cue: NodeCue | undefined = parsed?.find(
      (node) => node.type === "cue" && isActive(node),
    ) as NodeCue;
    if (cue) {
      const activeText = getText(cue.data);
      if (activeText !== text) setText(activeText);
    } else {
      setText("\n\n");
    }
  }, [position, parsed, isActive, text]);

  const loadCaptions = useCallback(async () => {
    const response = await fetch(captionsURL);
    const subtitles = await response.text();
    setParsed(parseSync(subtitles));
  }, [captionsURL]);

  useEffect(() => {
    if (!hasNoAudio && captionsURL !== "") {
      loadCaptions();
    }
  }, [loadCaptions, hasNoAudio, captionsURL]);

  if (hasNoAudio) {
    return (
      <CaptionsText
        data-testid="captions-text"
        dangerouslySetInnerHTML={{ __html: "[This slide intentionally has no audio.]" }}
      ></CaptionsText>
    );
  }

  return (
    <CaptionsText
      data-testid="captions-text"
      dangerouslySetInnerHTML={{ __html: sanitize(text) }}
    ></CaptionsText>
  );
}
